import { useEffect, useState } from 'react';
import { FolderPlus, BookOpen, Sparkles, X } from 'lucide-react';
import { useStore } from '../store';

const DISMISS_KEY = 'skema:welcome-dismissed';

/**
 * First-launch onboarding card. Sits centred on the canvas when:
 *   • the project is "fresh" (no nodes anywhere, no user-defined subsystems)
 *   • the user hasn't permanently dismissed it
 *
 * Two clear calls-to-action:
 *   1. "Add my first subsystem"   — opens the same modal as the project-tree + button
 *   2. "How does Skematiq work?"     — opens the Getting Started help tab
 *
 * The card vanishes the moment the user adds anything to the project, so
 * it doesn't get in the way once you start working. Click × to hide it
 * permanently.
 */
export function WelcomeBanner({
  onAddSubsystem,
  onLoadExample,
  onOpenGettingStarted,
}: {
  onAddSubsystem: () => void;
  onLoadExample: () => void;
  onOpenGettingStarted: () => void;
}) {
  const sheets = useStore((s) => s.sheets);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Detect "fresh project": no sheets at all (the new default — projects
  // start empty so the user can name their own subsystems). Old-style
  // projects with the four default sheets and no real content also count.
  const isFresh = (() => {
    if (sheets.length === 0) return true;
    const totalNodes = sheets.reduce((s, sh) => s + sh.nodes.length, 0);
    if (totalNodes > 0) return false;
    // 'Interconnect' kept for autosaves from before the sheet was renamed.
    const defaults = new Set(['Requirements', 'Block Diagram', 'Schematic', 'Interconnect', 'Harnesses']);
    if (sheets.length !== 4) return false;
    return sheets.every((s) => defaults.has(s.name));
  })();

  // If state moves out of fresh, also remember dismissal so the banner
  // doesn't pop back the next time the user happens to be staring at an
  // empty sheet.
  useEffect(() => {
    if (!isFresh) {
      try {
        localStorage.setItem(DISMISS_KEY, '1');
      } catch {
        // ignore
      }
    }
  }, [isFresh]);

  if (dismissed || !isFresh) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 460,
        maxWidth: '90%',
        background: 'var(--panel)',
        border: '1px solid var(--panel-border)',
        borderRadius: 'var(--radius)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.08)',
        padding: 20,
        zIndex: 8,
        pointerEvents: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 'var(--radius)',
            background: 'var(--accent-soft)',
            color: 'var(--accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FolderPlus size={20} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
            Welcome to Skematiq
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-muted)',
              lineHeight: 1.55,
              marginTop: 4,
            }}
          >
            Skematiq organises your machine as a tree of <strong>subsystems</strong>{' '}
            (Robot, Control Box, Sensor Pod…). Each subsystem groups its own{' '}
            <em>Requirements + Block + Schematic</em> sheets.
            <br />
            New here? <strong>Load the example project</strong> to explore a
            complete little design — or start your own from scratch.
          </div>
        </div>
        <button
          onClick={() => {
            try {
              localStorage.setItem(DISMISS_KEY, '1');
            } catch {
              // ignore
            }
            setDismissed(true);
          }}
          title="Dismiss — won't show again"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            background: 'transparent',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            borderRadius: 4,
            padding: 0,
          }}
        >
          <X size={16} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button onClick={onLoadExample} style={primaryBtn}>
          <Sparkles size={13} /> Load the example project
        </button>
        <button onClick={onAddSubsystem} style={secondaryBtn}>
          <FolderPlus size={13} /> Start from scratch
        </button>
      </div>
      <button onClick={onOpenGettingStarted} style={linkBtn}>
        <BookOpen size={13} /> How does Skematiq work?
      </button>
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  padding: '7px 12px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  flex: 1,
  justifyContent: 'center',
};

const secondaryBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '7px 12px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  flex: 1,
  justifyContent: 'center',
};

const linkBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'transparent',
  color: 'var(--text-muted)',
  border: 'none',
  padding: '10px 0 0',
  margin: '0 auto',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
